import {
  ALERT_STATUS,
  DEFAULT_PAGINATOR_SIZES,
  INSTANCE_TYPE,
  ROLE,
  WIDTH_NG,
} from "~/assets/js/constants";
import { createPaginator, isAllowedTo } from "~/assets/js/utils";
import { AlertDTO, AlertRow } from "~/types/alert/alert";
import {
  Action,
  Column,
  PaginatedResponse,
  TableHeader,
  stateComponentOptions,
} from "~/types/types";

import { translateAlert } from "~/assets/js/logic/translateAlert";

export default class AlertsTransformer {
  static disableds = [];

  static table(
    alerts: PaginatedResponse<AlertDTO>,
    {
      instanceType = INSTANCE_TYPE.MAIN,
      role,
      i18n,
      sortable = true,
      actionable = true,
      short = false,
      isMultiAgencyUser = false,
    },
  ) {
    this.disableds = [];

    const paginator = createPaginator<AlertDTO>(alerts, i18n, DEFAULT_PAGINATOR_SIZES);

    let sizes: string[] = [];

    if (isAllowedTo([ROLE.ADMIN, ROLE.MANAGER, ROLE.SERVICEDESK], role) || isMultiAgencyUser) {
      sizes = [
        short ? WIDTH_NG.MD : WIDTH_NG.LG,
        short ? WIDTH_NG.MD : WIDTH_NG.LG,
        short ? WIDTH_NG.MD : WIDTH_NG.LG,
        short ? WIDTH_NG["2XL"] : WIDTH_NG["3XL"],
        WIDTH_NG.LG,
      ];
    } else {
      sizes = [WIDTH_NG.LG, WIDTH_NG.LG, WIDTH_NG.XL, WIDTH_NG.LG];
    }

    const headers: TableHeader[] = [];
    headers.push(
      {
        id: "vaccine.name",
        label: i18n.t("alerts.headers.vaccineName"),
        loading: false,
        sortable,
      },
      {
        id: idFromRole(role, isMultiAgencyUser),
        label: i18n.t(headerTitleFromRole(role, isMultiAgencyUser)),
        loading: false,
        sortable:
          sortable && headerTitleFromRole(role, isMultiAgencyUser) !== "alerts.headers.agencyName",
      },
    );

    if (isAllowedTo([ROLE.ADMIN, ROLE.MANAGER, ROLE.SERVICEDESK], role) || isMultiAgencyUser) {
      headers.push({
        id: "agent.computerName",
        label: i18n.t("alerts.headers.computerName"),
        loading: false,
        sortable,
      });
    }

    headers.push(
      {
        id: "msg",
        label: i18n.t("alerts.headers.message"),
        loading: false,
        sortable,
      },
      {
        id: "createdOn",
        label: i18n.t("alerts.headers.date"),
        loading: false,
        sortable,
      },
    );

    return {
      paginator,
      sizes,
      headers,
      main: "vaccine.name",
      rows: alerts.collection.map((item) =>
        this.row(item, { role, instanceType, i18n, actionable, isMultiAgencyUser, short }),
      ),
      disableds: this.disableds,
    };
  }

  static row(
    alert: AlertDTO,
    { role, instanceType, i18n, actionable, isMultiAgencyUser, short = false },
  ): AlertRow {
    const { id, createdOn, updatedOn, msg, agent, vaccine, agency, status } = alert;

    const actions: Action[] = [];

    const shouldShowActions =
      actionable && status === ALERT_STATUS.ACTIVE && instanceType === INSTANCE_TYPE.MAIN;

    if (shouldShowActions) {
      if (isAllowedTo([ROLE.USER, ROLE.ADMIN, ROLE.MANAGER, ROLE.SERVICEDESK], role)) {
        actions.push(
          {
            active: false,
            icon: "s2-icon-check-solid",
            id: "alert-resolve",
            loading: false,
            label: i18n.t("alerts.resolve"),
          },
          {
            active: false,
            icon: "s2-icon-triangle-exclamation-solid",
            id: "alert-reject",
            loading: false,
            label: i18n.t("alerts.reject"),
          },
        );
      }
    }

    if (!short && alert.template !== "agentUninstalled") {
      actions.push({
        active: false,
        id: "alert-open",
        icon: "s2-icon-eye-regular",
        label: i18n.t(`generic.open`),
        loading: false,
      });
    }

    const computer = agent.computerName;

    const columns: Column[] = [];
    columns.push({
      data: {
        label: `${vaccine.name}`,
      },
      id: "vaccine.name",
      type: "text",
    });

    columns.push({
      data: {
        label: `${alertContext(alert, role, isMultiAgencyUser)}`,
      },
      id: idFromRole(role, isMultiAgencyUser),
      type: "text",
    });

    if (isAllowedTo([ROLE.ADMIN, ROLE.MANAGER, ROLE.SERVICEDESK], role) || isMultiAgencyUser) {
      columns.push({
        data: {
          label: computer,
        },
        id: "agent.computerName",
        type: "text",
      });
    }

    columns.push(
      {
        data: {
          label: translateAlert(alert, i18n),
        },
        id: "msg",
        type: "text",
      },
      {
        data: {
          label: i18n.d(new Date(createdOn * 1000), "medium"),
        },
        id: "createdOn",
        type: "text",
      },
    );

    const inactive = status === ALERT_STATUS.REJECTED || status === ALERT_STATUS.RESOLVED;

    if (inactive) {
      this.disableds.push(id);
    }

    return {
      id,
      createdOn,
      updatedOn,
      msg,
      status,
      actions,
      computer,
      vaccine: vaccine.name,
      agency: agency.name,
      date: createdOn,
      inactive,
      columns,
      ...iconAndStateFromStatus(status),
    };
  }
}

function iconAndStateFromStatus(status: ALERT_STATUS): {
  icon?: string;
  state?: stateComponentOptions;
} {
  switch (status) {
    case ALERT_STATUS.ACTIVE:
      return { state: "error" };
    case ALERT_STATUS.REJECTED:
      return { state: "warning" };
    case ALERT_STATUS.RESOLVED:
      return { icon: "s2-icon-check-solid", state: "ok" };
  }
}

function alertContext(alert: AlertDTO, role: ROLE, isMultiAgencyUser: boolean) {
  if (isMultiAgencyUser) {
    return `${alert.agency.name}`;
  } else {
    return isAllowedTo([ROLE.ADMIN, ROLE.MANAGER, ROLE.SERVICEDESK], role)
      ? `${alert.agency.name}`
      : `${alert.agent.computerName}`;
  }
}

function headerTitleFromRole(role: ROLE, isMultiAgencyUser: boolean) {
  if (isMultiAgencyUser) {
    return `alerts.headers.agencyName`;
  } else {
    return isAllowedTo([ROLE.ADMIN, ROLE.MANAGER, ROLE.SERVICEDESK], role)
      ? `alerts.headers.agencyName`
      : `alerts.headers.computerName`;
  }
}

function idFromRole(role: ROLE, isMultiAgencyUser: boolean) {
  if (isMultiAgencyUser) {
    return `agency.name`;
  } else {
    return isAllowedTo([ROLE.ADMIN, ROLE.MANAGER, ROLE.SERVICEDESK], role)
      ? `agency.name`
      : `agent.computerName`;
  }
}
